/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface VehicleModelProps {
  id: string;
  name: string;
  cruiseSpeedInMph: number | null;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class VehicleModelRecord extends Record<VehicleModelProps>({
  id: null as any,
  name: null as any,
  cruiseSpeedInMph: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    cruiseSpeedInMph: number | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): VehicleModelRecord {
    if (json == null) {
      throw new Error('VehicleModelRecord.fromJS: parameter cannot be null');
    }
    return new VehicleModelRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'VehicleModelJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'VehicleModelJson.name'),
      cruiseSpeedInMph: json.cruiseSpeedInMph,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    cruiseSpeedInMph: number | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [VehicleModelRecord, Error[]] {
    if (json == null) {
      throw new Error('VehicleModelRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new VehicleModelRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'VehicleModelJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'VehicleModelJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      cruiseSpeedInMph: tryOrDefault(
        () => json.cruiseSpeedInMph,
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: VehicleModelProps) {
    super(props);
    if (props == null) {
      throw new Error('VehicleModelRecord constructor parameter cannot be null');
    }
  }
}

export abstract class VehicleModelViewModelBase implements VehicleModelProps {
  protected readonly _props: VehicleModelProps;
  private _json?: VehicleModelProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get cruiseSpeedInMph(): number | null {
    return this._props.cruiseSpeedInMph;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: VehicleModelProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        cruiseSpeedInMph: this._props.cruiseSpeedInMph,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllVehicleModelEntities = (state: AppState) =>
  state.entityStores.vehicleModel.entities;

export const selectVehicleModelById = (id: string) => (state: AppState) =>
  state.entityStores.vehicleModel.entities.get(id);

export const selectVehicleModelMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.vehicleModel.meta.get(id);

export const selectVehicleModelAndMetadataById = (id: string) =>
  createSelector(
    selectVehicleModelById(id),
    selectVehicleModelMetadataById(id),
    (vehicleModel, meta) => ({ vehicleModel, meta }),
  );

export function selectVehicleModelEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllVehicleModelEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, VehicleModelRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectVehicleModelEntitiesByName(name: string) {
  return createSelector(selectAllVehicleModelEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectVehicleModelEntitiesByCruiseSpeedInMph(cruiseSpeedInMph: number) {
  return createSelector(selectAllVehicleModelEntities, (entities) =>
    entities.filter((e) => e.cruiseSpeedInMph === cruiseSpeedInMph).valueSeq(),
  );
}
//#endregion Selectors
